import { useState } from 'react'

// Contexts
import { useInstructionalMRT } from '../../../contexts/InstructionalMRTContext'

// MUI Components
import { 
    Box, 
    Typography, 
    TextField,
    Modal, 
    IconButton,
    styled
} from '@mui/material';

// MUI Icons
import CloseIcon from '@mui/icons-material/Close'

// Components
import AsynchronousActionButton from '../../../components/Buttons/AsynchronousActionButton'

// Styled Components
const StyledTypography = styled(Typography)(({ theme }) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#374151',
    minHeight: '36px',
    maxHeight: '72px'
}));


const RenameFileModal = ({ 
    open, 
    onClose,
    modalTitle,
    selected,
    updateSelected,
    saveChanges,
    clearSelected,
    hasChanges,
    value
}) => {
    const [loading, setLoading] = useState(false)

    const handleEditSectionName = (event) => {
        updateSelected({ [value]: event.target.value })
    }

    const handleClose = () => {
        clearSelected()
        onClose()
    }

    const handleSave = async () => {
        setLoading(true)
        try {
            await saveChanges()
            onClose()
        } catch (error) {
            console.error("Error saving changes: ", error)
        } finally {
            setLoading(false)
        }
    }

    const hasChanged = hasChanges()

    return (
        <Modal
            open={open}
            onClose={handleClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '548px',
                    height: '320px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                    padding: '24px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px'
                }}
            >
                {/* Main Content Container - All Subcomponents */}
                <Box
                    sx={{
                        flexGrow: 1,
                        mb: '112px', // Space for fixed save button
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                    }}
                >
                    {/* Header Subcomponent - Main Container Frame: 500px × 66px */}
                    <Box 
                        sx={{ 
                            width: '100%',
                            maxHeight: '102px',
                            minHeight: '66px',
                            mb: '4px',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        {/* Title and Content Toggle Frame: 500px × 36px */}
                        <Box
                            sx={{
                                width: '100%',
                                // height: '100%',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                // gap: '8px'
                                mt: '-4px'
                            }}
                        >
                            
                            {/* Title */}
                            <StyledTypography>
                                Rename {modalTitle}
                            </StyledTypography>
                        </Box>

                        {/* Close Button Frame: 500px × 30px */}
                        <Box
                            sx={{
                                
                                height: '100%',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-end'
                            }}
                        >
                            <IconButton
                                onClick={handleClose}
                                sx={{
                                    width: '16px',
                                    height: '16px',
                                    padding: 0,
                                    minWidth: '16px',
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: '#F3F4F6',
                                        borderRadius: '6px'
                                    }
                                }}
                            >
                                <CloseIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                            </IconButton>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            mb: '16px',
                            flexShrink: 0
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '12px',
                                color: '#6B7280',
                                fontWeight: 700,
                                lineHeight: '18px',
                                mb: '4px'
                            }}
                        >
                            {modalTitle} Name:
                        </Typography>
                        <TextField
                            value={selected && selected[value] ? selected[value] : ''}
                            onChange={handleEditSectionName}
                            size="large"
                            sx={{
                                width: '500px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    backgroundColor: '#FFFFFF',
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    color: '#6B7280',
                                    padding: 0,
                                    '& fieldset': {
                                        borderColor: '#E5E7EB',
                                        borderWidth: '1px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#E5E7EB'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#4F46E5',
                                        borderWidth: '1px'
                                    }
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '10px 12px',
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    color: '#6B7280',
                                }
                            }}
                        />
                    </Box>
                </Box>

                {/* Save Button */}
                <AsynchronousActionButton
                    onClick={handleSave}
                    disabled={!hasChanged}
                    loading={loading}
                    sx={{
                        position: 'absolute',
                        bottom: '40px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '375px',
                        height: '48px',
                        backgroundColor: hasChanged ? '#1F477D' : '#E5E7EB',
                        color: hasChanged ? '#FFFFFF' : '#6B7280',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: 500,
                        textTransform: 'none',
                        zIndex: 2,
                        '&:hover': {
                            backgroundColor: hasChanged ? '#163A63' : '#E5E7EB'
                        },
                        '&:disabled': {
                            backgroundColor: '#E5E7EB',
                            color: '#6B7280'
                        }
                    }}
                >
                    { loading ? "" : "Save" }
                </AsynchronousActionButton>
            </Box>
        </Modal>
    )
}

export default RenameFileModal